import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import Lightbox from '../components/Lightbox';

export default function ServiceDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [service, setService] = useState(null);
  const [galleryItems, setGalleryItems] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [asbestPosterOpen, setAsbestPosterOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // Fetch service info from CMS
    fetch('https://api.dev.auxo.dk/public/content/ydelser')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          const match = data.find(item => item.ydelse_slug === slug);
          if (match) {
            setService(match);
          } else {
            // If invalid slug, redirect to 404
            navigate('/404', { replace: true });
          }
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Fejl ved hentning af ydelse:', err);
        setLoading(false);
      });

    // Fetch gallery items filtered by category slug
    fetch('https://api.dev.auxo.dk/public/content/galleri')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          const filtered = data.filter(g => g.kategori_slug === slug);
          setGalleryItems(filtered);
        }
      })
      .catch(err => console.error('Fejl ved hentning af galleri:', err));

  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-[500px] flex items-center justify-center bg-white">
        <div className="text-gray-500 font-medium flex items-center gap-3">
          <i className="fa-solid fa-[#3A5A40] fa-spinner fa-spin text-2xl"></i>
          Henter information...
        </div>
      </div>
    );
  }

  if (!service) return null;

  const heroImgUrl = Array.isArray(service.hero_billede) ? service.hero_billede[0] : service.hero_billede;
  const resizedHero = heroImgUrl ? heroImgUrl.replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/') : '';

  // Parse bulletpoints string into array if separated by newline
  const bulletList = service.bulletpoints ? service.bulletpoints.split('\n').filter(Boolean) : [];

  return (
    <>
      <SEO 
        title={service.titel} 
        description={service.kort_beskrivelse || `Professionelt tømrerarbejde inden for ${service.titel.toLowerCase()} udført af Fyns - Tømreren ApS på hele Fyn.`} 
        image={resizedHero || undefined}
      />

      {/* Hero Header */}
      <section className="relative min-h-[400px] flex items-center justify-center bg-[#1F2A37] text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={resizedHero}
            alt={`${service.titel} – Professionelt tømrer- og snedkerarbejde udført af Fyns - Tømreren ApS`}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1F2A37] via-[#1F2A37]/80 to-transparent"></div>
          <div className="absolute bottom-3 right-4 z-10 hidden sm:flex items-center gap-1.5 px-3 py-1 bg-black/60 backdrop-blur-xs text-white/90 text-xs rounded-none border border-white/10">
            <i className="fa-solid fa-camera text-[10px] text-[#3A5A40]"></i>
            <span>Reference: {service.titel} på Fyn</span>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight mb-4 text-white">
            {service.titel}
          </h1>
          <p className="text-lg text-gray-200 max-w-2xl mx-auto leading-relaxed">
            {service.kort_beskrivelse}
          </p>
        </div>
      </section>

      {/* Details & Bulletpoints */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            
            {/* Main Content & Bulletpoints */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#111111] mb-4">
                  Professionel løsning tilpasset dine ønsker
                </h2>
                <p className="text-gray-700 leading-relaxed text-base mb-6">
                  Når du lader <Link to="/" className="text-[#3A5A40] hover:text-[#2D4632] underline decoration-1 underline-offset-2 font-semibold" title="Gå til forsiden for Fyns Tømreren">Fyns - Tømreren ApS</Link> stå for dit projekt inden for {service.titel.toLowerCase()}, er du garanteret professionelt udført håndværk fra start til slut. Vi rådgiver gerne om materialevalg, konstruktionsmuligheder og fremtidig vedligeholdelse.
                </p>
              </div>

              {bulletList.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-[#1F2A37]">
                    Hvad vi tilbyder inden for {service.titel}:
                  </h3>
                  <ul className="space-y-3">
                    {bulletList.map((bp, i) => (
                      <li key={i} className="flex items-start gap-3 text-gray-800 text-sm sm:text-base">
                        <i className="fa-solid fa-check text-[#3A5A40] mt-1 text-sm"></i>
                        <span>{bp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Asbest Specialized Content */}
              {service.ydelse_slug === 'asbestsanering' && (
                <div className="space-y-8 pt-4">
                  {/* Info Box */}
                  <div className="bg-[#17202A] text-white p-6 sm:p-8 rounded-xs border-l-4 border-[#d75d5a] space-y-4 shadow-md">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#d75d5a]/20 border border-[#d75d5a] text-[#f27a77] text-xs font-bold uppercase tracking-wider rounded-xs">
                      <i className="fa-solid fa-shield-halved"></i>
                      Godkendt af Arbejdstilsynet
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-white">
                      Lovpligtige krav til asbestsanering
                    </h3>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      Fra 1. januar 2025 kræver lovgivningen, at nedtagning af asbestholdige materialer (f.eks. bølgeeternittage og skiferbeklædninger) udelukkende må udføres af virksomheder med officiel autorisation. Hos Fyns-Tømreren ApS har vi autorisationen på plads og sikrer fuld overholdelse af alle sikkerheds- og miljøkrav.
                    </p>
                  </div>

                  {/* Samlet Kampagne-sektion (Alle elementer samlet i én genkendelig ramme) */}
                  <div className="relative border-2 border-[#d75d5a]/60 bg-[#F4F6F8] rounded-xs p-4 sm:p-6 lg:p-8 space-y-8 shadow-lg">
                    
                    {/* 1. Kampagne Hero-sektion */}
                    <div className="relative overflow-hidden bg-gradient-to-br from-[#1F2A37] via-[#111827] to-[#0f172a] text-white p-6 sm:p-8 rounded-xs border border-gray-700 shadow-xl space-y-6">
                      <div className="space-y-4">
                        {/* Lille fremhævet tekst */}
                        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#d75d5a] text-white text-xs font-extrabold uppercase tracking-widest rounded-xs shadow-sm">
                          <i className="fa-solid fa-bolt"></i>
                          NY KAMPAGNE
                        </div>

                        {/* Stor hovedoverskrift */}
                        <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight leading-tight">
                          AUTORISERET ASBESTNEDTAGNING PÅ FYN
                        </h3>

                        {/* Brødtekst */}
                        <p className="text-gray-200 text-base sm:text-lg leading-relaxed">
                          Hos Fyns-Tømreren ApS tilbyder vi professionel og autoriseret nedtagning af asbestholdige tage til faste og gennemskuelige m²-priser.
                        </p>

                        {/* Kampagnebadge med særligt tydelig dato */}
                        <div className="bg-gradient-to-r from-amber-500/20 via-[#d75d5a]/25 to-amber-500/20 border-2 border-amber-400 p-4 sm:p-5 rounded-xs shadow-lg">
                          <div className="flex items-center gap-3">
                            <i className="fa-solid fa-clock-rotate-left text-amber-400 text-2xl flex-shrink-0"></i>
                            <div className="text-sm sm:text-base font-bold text-white tracking-wide uppercase">
                              KAMPAGNEN GÆLDER KUN FREM TIL <span className="text-amber-300 text-lg sm:text-xl font-black bg-black/40 px-2 py-0.5 rounded-xs border border-amber-400/50 inline-block ml-1 mr-1 shadow-xs">30. SEPTEMBER</span> 2026
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 2. Priser */}
                    <div className="space-y-4 pt-2">
                      <div className="border-b border-gray-300 pb-2">
                        <h4 className="text-xl sm:text-2xl font-black text-[#111111] uppercase tracking-tight flex items-center gap-2">
                          <i className="fa-solid fa-tags text-[#d75d5a]"></i>
                          Priser
                        </h4>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        
                        {/* Prisboks 1 */}
                        <div className="flex flex-col justify-between bg-white border-2 border-gray-300 rounded-xs p-5 shadow-sm hover:shadow-md transition-shadow">
                          <div>
                            <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                              KUN NEDTAGNING
                            </div>
                            <h5 className="text-base sm:text-lg font-black text-[#111111] uppercase tracking-tight mb-4">
                              BØLGEETERNIT
                            </h5>
                            
                            <div className="bg-gray-100 p-3 rounded-xs text-center mb-5 border border-gray-200">
                              <div className="text-2xl sm:text-3xl font-black text-[#111111] tracking-tight">
                                250 <span className="text-sm font-bold">KR./M²</span>
                              </div>
                              <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">+ MOMS</span>
                            </div>

                            <div className="space-y-2 mb-4">
                              <div className="text-xs font-bold text-[#111111] uppercase tracking-wide">
                                Kunden står selv for:
                              </div>
                              <ul className="space-y-1.5 text-xs sm:text-sm text-gray-700">
                                <li className="flex items-center gap-2">
                                  <i className="fa-solid fa-minus text-gray-400 text-xs"></i>
                                  Stillads
                                </li>
                                <li className="flex items-center gap-2">
                                  <i className="fa-solid fa-minus text-gray-400 text-xs"></i>
                                  Indpakning af stillads
                                </li>
                                <li className="flex items-center gap-2">
                                  <i className="fa-solid fa-minus text-gray-400 text-xs"></i>
                                  Miljøvogn
                                </li>
                                <li className="flex items-center gap-2">
                                  <i className="fa-solid fa-minus text-gray-400 text-xs"></i>
                                  Deponi
                                </li>
                              </ul>
                            </div>
                          </div>

                          <div className="pt-3 border-t border-gray-200 mt-4 text-xs font-bold text-[#3A5A40] flex items-center gap-1.5">
                            <i className="fa-solid fa-circle-check"></i>
                            <span>Fyns-Tømreren ApS står for indberetningen.</span>
                          </div>
                        </div>

                        {/* Prisboks 2 */}
                        <div className="flex flex-col justify-between bg-white border-2 border-gray-300 rounded-xs p-5 shadow-sm hover:shadow-md transition-shadow">
                          <div>
                            <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                              KUN NEDTAGNING
                            </div>
                            <h5 className="text-base sm:text-lg font-black text-[#111111] uppercase tracking-tight mb-4">
                              SKIFERETERNIT
                            </h5>
                            
                            <div className="bg-gray-100 p-3 rounded-xs text-center mb-5 border border-gray-200">
                              <div className="text-2xl sm:text-3xl font-black text-[#111111] tracking-tight">
                                400 <span className="text-sm font-bold">KR./M²</span>
                              </div>
                              <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">+ MOMS</span>
                            </div>

                            <div className="space-y-2 mb-4">
                              <div className="text-xs font-bold text-[#111111] uppercase tracking-wide">
                                Kunden står selv for:
                              </div>
                              <ul className="space-y-1.5 text-xs sm:text-sm text-gray-700">
                                <li className="flex items-center gap-2">
                                  <i className="fa-solid fa-minus text-gray-400 text-xs"></i>
                                  Stillads
                                </li>
                                <li className="flex items-center gap-2">
                                  <i className="fa-solid fa-minus text-gray-400 text-xs"></i>
                                  Indpakning af stillads
                                </li>
                                <li className="flex items-center gap-2">
                                  <i className="fa-solid fa-minus text-gray-400 text-xs"></i>
                                  Miljøvogn
                                </li>
                                <li className="flex items-center gap-2">
                                  <i className="fa-solid fa-minus text-gray-400 text-xs"></i>
                                  Deponi
                                </li>
                              </ul>
                            </div>
                          </div>

                          <div className="pt-3 border-t border-gray-200 mt-4 text-xs font-bold text-[#3A5A40] flex items-center gap-1.5">
                            <i className="fa-solid fa-circle-check"></i>
                            <span>Fyns-Tømreren ApS står for indberetningen.</span>
                          </div>
                        </div>

                        {/* Prisboks 3 (Komplet løsning) */}
                        <div className="flex flex-col justify-between bg-gradient-to-b from-[#1F2A37] to-[#111827] text-white border-2 border-[#d75d5a] rounded-xs p-5 shadow-xl relative overflow-hidden">
                          <div className="absolute top-0 right-0 bg-[#d75d5a] text-white text-[10px] font-black uppercase px-2.5 py-0.5 tracking-wider rounded-bl-xs shadow-xs">
                            KOMPLET LØSNING
                          </div>
                          <div>
                            <div className="text-xs font-extrabold text-[#f27a77] uppercase tracking-wider mb-1 flex items-center gap-1">
                              <i className="fa-solid fa-star text-amber-400 text-[10px]"></i>
                              KOMPLET LØSNING
                            </div>
                            <h5 className="text-base sm:text-lg font-black text-white uppercase tracking-tight mb-4">
                              BØLGEETERNIT
                            </h5>
                            
                            <div className="bg-black/50 p-3 rounded-xs text-center mb-5 border border-white/15">
                              <div className="text-xs font-black text-amber-300 uppercase tracking-widest mb-0.5">
                                FRA KUN
                              </div>
                              <div className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                                400 <span className="text-sm font-bold text-gray-300">KR./M²</span>
                              </div>
                              <span className="text-xs font-bold text-gray-300 uppercase tracking-wider">+ MOMS</span>
                            </div>

                            <div className="space-y-2 text-xs sm:text-sm text-gray-200">
                              <p className="font-bold text-white">
                                Vi klarer hele opgaven fra A-Z.
                              </p>
                              <p className="text-gray-300 leading-relaxed text-xs">
                                Kontakt os og hør mere om, hvad løsningen indeholder.
                              </p>
                            </div>
                          </div>

                          <div className="pt-3 border-t border-gray-700 mt-4 text-xs font-semibold text-amber-300 flex items-center gap-1.5">
                            <i className="fa-solid fa-shield-halved text-[#d75d5a]"></i>
                            <span>Komplet & tryg løsning</span>
                          </div>
                        </div>

                        {/* Prisboks 4 (Komplet løsning) */}
                        <div className="flex flex-col justify-between bg-gradient-to-b from-[#1F2A37] to-[#111827] text-white border-2 border-[#d75d5a] rounded-xs p-5 shadow-xl relative overflow-hidden">
                          <div className="absolute top-0 right-0 bg-[#d75d5a] text-white text-[10px] font-black uppercase px-2.5 py-0.5 tracking-wider rounded-bl-xs shadow-xs">
                            KOMPLET LØSNING
                          </div>
                          <div>
                            <div className="text-xs font-extrabold text-[#f27a77] uppercase tracking-wider mb-1 flex items-center gap-1">
                              <i className="fa-solid fa-star text-amber-400 text-[10px]"></i>
                              KOMPLET LØSNING
                            </div>
                            <h5 className="text-base sm:text-lg font-black text-white uppercase tracking-tight mb-4">
                              SKIFERETERNIT
                            </h5>
                            
                            <div className="bg-black/50 p-3 rounded-xs text-center mb-5 border border-white/15">
                              <div className="text-xs font-black text-amber-300 uppercase tracking-widest mb-0.5">
                                FRA KUN
                              </div>
                              <div className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                                550 <span className="text-sm font-bold text-gray-300">KR./M²</span>
                              </div>
                              <span className="text-xs font-bold text-gray-300 uppercase tracking-wider">+ MOMS</span>
                            </div>

                            <div className="space-y-2 text-xs sm:text-sm text-gray-200">
                              <p className="font-bold text-white">
                                Vi klarer hele opgaven fra A-Z.
                              </p>
                              <p className="text-gray-300 leading-relaxed text-xs">
                                Kontakt os og hør mere om, hvad løsningen indeholder.
                              </p>
                            </div>
                          </div>

                          <div className="pt-3 border-t border-gray-700 mt-4 text-xs font-semibold text-amber-300 flex items-center gap-1.5">
                            <i className="fa-solid fa-shield-halved text-[#d75d5a]"></i>
                            <span>Komplet & tryg løsning</span>
                          </div>
                        </div>

                      </div>
                    </div>

                    {/* 3. Fordele / Tryghedspunkter */}
                    <div className="space-y-4 pt-2">
                      <div className="border-b border-gray-300 pb-2">
                        <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider">
                          Fordele / tryghedspunkter
                        </h4>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                        {/* 1. Autoriseret */}
                        <div className="bg-white border border-gray-300 p-4 rounded-xs text-center flex flex-col items-center justify-center space-y-2 hover:border-[#3A5A40] transition-colors shadow-xs">
                          <div className="w-10 h-10 rounded-full bg-[#3A5A40]/10 text-[#3A5A40] flex items-center justify-center text-lg">
                            <i className="fa-solid fa-shield-check"></i>
                          </div>
                          <div className="text-xs font-extrabold text-[#111111] uppercase tracking-tight">
                            AUTORISERET ASBESTARBEJDE
                          </div>
                        </div>

                        {/* 2. Sikker nedtagning */}
                        <div className="bg-white border border-gray-300 p-4 rounded-xs text-center flex flex-col items-center justify-center space-y-2 hover:border-[#3A5A40] transition-colors shadow-xs">
                          <div className="w-10 h-10 rounded-full bg-[#3A5A40]/10 text-[#3A5A40] flex items-center justify-center text-lg">
                            <i className="fa-solid fa-certificate"></i>
                          </div>
                          <div className="text-xs font-extrabold text-[#111111] uppercase tracking-tight">
                            SIKKER OG KORREKT NEDTAGNING
                          </div>
                        </div>

                        {/* 3. Indberetning */}
                        <div className="bg-white border border-gray-300 p-4 rounded-xs text-center flex flex-col items-center justify-center space-y-2 hover:border-[#3A5A40] transition-colors shadow-xs">
                          <div className="w-10 h-10 rounded-full bg-[#3A5A40]/10 text-[#3A5A40] flex items-center justify-center text-lg">
                            <i className="fa-solid fa-clipboard-check"></i>
                          </div>
                          <div className="text-xs font-extrabold text-[#111111] uppercase tracking-tight">
                            VI SØRGER FOR DEN LOVPLIGTIGE INDBERETNING
                          </div>
                        </div>

                        {/* 4. Faste priser */}
                        <div className="bg-white border border-gray-300 p-4 rounded-xs text-center flex flex-col items-center justify-center space-y-2 hover:border-[#3A5A40] transition-colors shadow-xs">
                          <div className="w-10 h-10 rounded-full bg-[#3A5A40]/10 text-[#3A5A40] flex items-center justify-center text-lg">
                            <i className="fa-solid fa-tags"></i>
                          </div>
                          <div className="text-xs font-extrabold text-[#111111] uppercase tracking-tight">
                            FASTE M²-PRISER – INGEN OVERRASKELSER
                          </div>
                        </div>

                        {/* 5. Hele Fyn */}
                        <div className="bg-white border border-gray-300 p-4 rounded-xs text-center flex flex-col items-center justify-center space-y-2 hover:border-[#3A5A40] transition-colors shadow-xs">
                          <div className="w-10 h-10 rounded-full bg-[#3A5A40]/10 text-[#3A5A40] flex items-center justify-center text-lg">
                            <i className="fa-solid fa-location-dot"></i>
                          </div>
                          <div className="text-xs font-extrabold text-[#111111] uppercase tracking-tight">
                            VI KØRER PÅ HELE FYN
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 4. Kontakt / CTA */}
                    <div className="bg-gradient-to-br from-[#1F2A37] to-[#111827] text-white p-6 sm:p-8 rounded-xs border-2 border-gray-700 shadow-xl space-y-6">
                      <div className="text-center sm:text-left space-y-2">
                        <h4 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight">
                          KONTAKT OS I DAG FOR ET UFORPLIGTENDE TILBUD!
                        </h4>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-gray-700/80">
                        {/* Telefon */}
                        <a
                          href="tel:60715018"
                          className="flex items-center gap-3 p-3.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xs transition-colors group"
                        >
                          <div className="w-10 h-10 rounded-full bg-[#d75d5a] flex items-center justify-center text-white flex-shrink-0 group-hover:scale-105 transition-transform">
                            <i className="fa-solid fa-phone"></i>
                          </div>
                          <div>
                            <div className="text-[11px] uppercase text-gray-400 font-bold">Telefon</div>
                            <div className="text-sm sm:text-base font-black text-white whitespace-nowrap">
                              60 71 50 18
                            </div>
                          </div>
                        </a>

                        {/* E-mail */}
                        <a
                          href="mailto:fynstomreren@gmail.com"
                          className="flex items-center gap-3 p-3.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xs transition-colors group"
                        >
                          <div className="w-10 h-10 rounded-full bg-[#3A5A40] flex items-center justify-center text-white flex-shrink-0 group-hover:scale-105 transition-transform">
                            <i className="fa-solid fa-envelope"></i>
                          </div>
                          <div className="min-w-0">
                            <div className="text-[11px] uppercase text-gray-400 font-bold">E-mail</div>
                            <div className="text-xs sm:text-sm font-bold text-white truncate">
                              fynstomreren@gmail.com
                            </div>
                          </div>
                        </a>

                        {/* Facebook */}
                        <a
                          href="https://www.facebook.com"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xs transition-colors group"
                        >
                          <div className="w-10 h-10 rounded-full bg-[#1877F2] flex items-center justify-center text-white flex-shrink-0 group-hover:scale-105 transition-transform">
                            <i className="fa-brands fa-facebook-f"></i>
                          </div>
                          <div>
                            <div className="text-[11px] uppercase text-gray-400 font-bold">Facebook</div>
                            <div className="text-sm font-bold text-white whitespace-nowrap">
                              Følg os på Facebook
                            </div>
                          </div>
                        </a>
                      </div>

                      <div className="pt-2 text-center sm:text-left">
                        <Link
                          to="/kontakt"
                          className="inline-flex items-center justify-center gap-2 bg-[#d75d5a] hover:bg-[#c24b48] text-white px-8 py-3.5 font-bold text-sm sm:text-base rounded-xs shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
                        >
                          <span>Få et uforpligtende tilbud</span>
                          <i className="fa-solid fa-arrow-right"></i>
                        </Link>
                      </div>
                    </div>

                    {/* 5. Afsluttende tryghedsbudskab */}
                    <div className="bg-[#3A5A40] text-white py-4 px-6 rounded-xs shadow-md text-center border-l-4 border-emerald-300">
                      <p className="text-sm sm:text-base md:text-lg font-black uppercase tracking-wider flex items-center justify-center gap-2 sm:gap-3">
                        <i className="fa-solid fa-shield-heart text-emerald-200"></i>
                        <span>VI TAGER ANSVAR – SÅ DU KAN VÆRE TRYG</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar Contact Card */}
            <div className="space-y-6">
              <div className="bg-[#1F2A37] text-white p-8 shadow-md space-y-6">
                <h3 className="text-xl font-bold border-b border-gray-700 pb-4">
                  Få et uforpligtende tilbud
                </h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Har du spørgsmål eller ønsker du en pris på tømrerarbejde i Munkebo eller øvrige Fyn? Vi står klar til at hjælpe.
                </p>
                
                <div className="space-y-4 pt-2">
                  <a
                    href="tel:60715018"
                    className="block w-full text-center bg-[#d75d5a] hover:bg-[#c24b48] text-white py-3 px-4 font-semibold text-sm transition-colors whitespace-nowrap shadow-sm"
                  >
                    <i className="fa-solid fa-phone mr-2"></i>
                    Ring: <span className="whitespace-nowrap">60 71 50 18</span>
                  </a>
                  <Link
                    to="/kontakt"
                    className="block w-full text-center bg-gray-800 hover:bg-gray-700 text-white py-3 px-4 font-semibold text-sm transition-all duration-300 hover:-translate-y-1"
                  >
                    Skriv til os via kontaktformular
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Dynamic Gallery Section for this service */}
      {galleryItems.length > 0 && (
        <section className="py-20 bg-[#F5F5F5]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 text-center">
              <h2 className="text-3xl font-bold text-[#111111]">
                Eksempler på {service.titel}
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryItems.map((item, idx) => {
                const imgUrl = Array.isArray(item.billede) ? item.billede[0] : item.billede;
                const resized = imgUrl.replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/assets/');

                return (
                  <figure
                    key={item.id || idx}
                    onClick={() => setLightboxIndex(idx)}
                    className="group bg-white overflow-hidden cursor-pointer shadow-xs hover:shadow-lg transition-all duration-300 border border-gray-200 flex flex-col"
                  >
                    <div className="relative h-56 overflow-hidden bg-gray-100">
                      <img
                        src={resized}
                        alt={`${item.titel} – Eksempel på ${service.titel.toLowerCase()} udført af Fyns Tømreren`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute top-3 right-3 w-8 h-8 bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <i className="fa-solid fa-expand text-xs"></i>
                      </div>
                    </div>
                    <figcaption className="p-3.5 bg-gray-50 border-t border-gray-100 flex-1">
                      <div className="text-xs font-bold text-[#111111] line-clamp-1 group-hover:text-[#3A5A40] transition-colors">
                        {item.titel}
                      </div>
                      {item.beskrivelse && (
                        <p className="text-[11px] text-gray-500 line-clamp-2 mt-0.5 leading-relaxed">
                          {item.beskrivelse}
                        </p>
                      )}
                    </figcaption>
                  </figure>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox for Gallery */}
      {lightboxIndex !== null && (
        <Lightbox
          images={galleryItems}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((prev) => (prev > 0 ? prev - 1 : galleryItems.length - 1))}
          onNext={() => setLightboxIndex((prev) => (prev < galleryItems.length - 1 ? prev + 1 : 0))}
        />
      )}

      {/* Asbest Zoom Modal */}
      {asbestPosterOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center p-4 animate-fadeIn"
          onClick={() => setAsbestPosterOpen(false)}
        >
          <div className="absolute top-4 right-4 z-50 flex items-center gap-3">
            <button
              onClick={() => setAsbestPosterOpen(false)}
              className="px-4 py-2 bg-[#d75d5a] hover:bg-[#c24b48] text-white font-bold text-sm flex items-center gap-2 rounded-xs shadow-lg transition-colors cursor-pointer"
            >
              <i className="fa-solid fa-xmark text-base"></i>
              Luk visning
            </button>
          </div>

          <figure 
            className="max-w-4xl max-h-[90vh] overflow-auto flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src="https://assets.auxodev.dk/assets/933ba291-d562-4801-b20a-fae6de561b45/autoriseret-asbesthaandtering-og-sanering.png"
              alt="Autoriseret asbestsanering kampagne fra Fyns - Tømreren ApS med oplysninger om faste m²-priser og lovkrav"
              className="max-w-full max-h-[82vh] object-contain rounded-xs shadow-2xl border border-gray-700"
            />
            <figcaption className="text-center text-white text-xs mt-3 opacity-80">
              Autoriseret asbestsanering kampagne – Faste m²-priser, miljøgodkendt nedtagning og anmeldelse til Arbejdstilsynet
            </figcaption>
          </figure>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-[#1F2A37] text-white text-center">
        <div className="max-w-4xl mx-auto px-4 space-y-6">
          <h2 className="text-3xl font-bold">Klar til at opstarte dit projekt?</h2>
          <p className="text-gray-300 text-base">
            Kontakt Fyns - Tømreren ApS i dag for en uforpligtende samtale om {service.titel.toLowerCase()}.
          </p>
          <div className="pt-2">
            <Link
              to="/kontakt"
              className="inline-block bg-[#d75d5a] hover:bg-[#c24b48] text-white px-8 py-3.5 font-semibold text-base transition-all duration-300 hover:-translate-y-1 shadow-sm hover:shadow-xl"
            >
              Indhent tilbud nu
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
